import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { handleSession } from '../../api/apiSessionHandle';
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { getaxiosRequestWithToken, postaxiosRequestWithToken } from "../../api/api";
import { showToast } from '../../services/toastifyservices';
import endpoints from "../../api/EndPoints";

const SearchableSelect = ({ value, onChange, options, required, placeholder, styles, setSelectedValue }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [patientsList, setPatientsList] = useState([]);
  const wrapperRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const fetchPatientData = async () => {
    
    if (searchTerm.trim().length < 3) {
      setAllPatients([]);
      setPatientsList([]);
      setSelectedValue('');
      return;
    } try {
      const payload = { input: searchTerm };
      const endpoint = `${endpoints.searchpatient}?page=1&limit=1000`;
      const response = await getaxiosRequestWithToken(endpoint, payload);
      
      if (response.success) {
        const data = response.data.patients.data;
        setAllPatients(data);
        setPatientsList(data.map((patient) => `${patient.first_name} ${patient.last_name} | ${patient.dob}`));
      }
    } catch (error) {
      
      showToast('error', error.response?.data.message);
      handleSession(error.response?.data);
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setPatientsList([]);
      return;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      fetchPatientData();
    }, 200);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {

      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredOptions = patientsList.filter((patient) =>
    patient.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOptionSelect = useCallback(
    (option) => {
      if (!option) return;

      setSearchTerm(option);
      onChange(option); // Send the selected value to the parent component
      setIsDropdownOpen(false);

      const optionParts = option.split("|")[0].trim().split(/\s+/);
      const [optionFirstName, optionLastName] = optionParts;

      const selectedPatient = allPatients.find((patient) => {
        const patientFirstName = patient.first_name.trim();
        const patientLastName = patient.last_name.trim();

        return (
          patientFirstName.toLowerCase() === optionFirstName.toLowerCase() &&
          patientLastName.toLowerCase() === optionLastName.toLowerCase()
        );
      });
      setSelectedValue(selectedPatient.id)
    },
    [allPatients, onChange]
  );
  const handleBackspace = (e) => {
    if (e.key === "Backspace") {
      setSearchTerm("");
      setSelectedValue(''); // Reset the selected value
      setIsDropdownOpen(false); // Close the dropdown
    }
  };

  return (
    <div className="position-relative" ref={wrapperRef}>
      <input
        type="text"
        value={searchTerm}
        required={required}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setIsDropdownOpen(true);

        }}
        onKeyDown={handleBackspace}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        placeholder={placeholder || "Patient name"}
        className={clsx(
          "form-control shadow-sm border rounded-lg py-2 px-3 text-dark",
          styles?.control
        )}
      />
      <div
        className="cursor-pointer position-absolute top-0 end-0 d-flex align-items-center p-2 text-secondary"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <svg
          className="h-6 w-6"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.354a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      {isDropdownOpen && (
        <div className="position-absolute z-index-10 w-100 bg-white border rounded-lg shadow-lg" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => handleOptionSelect(option)}
                className={clsx(
                  "py-2 px-3 cursor-pointer hover:bg-primary hover:text-white",
                  {
                    "bg-primary text-white": value === option,
                    "bg-white text-dark": value !== option,
                  },
                  styles?.option
                )}
              >
                {option}
              </div>
            ))
          ) : (
            <div className="py-2 px-3 text-muted">Please Search Here</div>
          )}
        </div>
      )}
    </div>
  );
};



export default SearchableSelect;
